(() => {
  'use strict';

  const VERSION = 'r12';
  const LOOP_KEY = 'ottrecord-player-loop-r12';
  const LEGACY_LOOP_KEY = 'ottrecord-player-loop-r11';
  const QUEUE_KEY = 'ottrecord-player-queue-r12';
  const STATE_KEY = 'ottrecord-player-state-r12';
  const STATE_MAX_AGE = 30 * 60 * 1000;
  const MAX_PREVIEW = 90;
  const audioFallback = {
    "play": "Écouter",
    "pause": "Pause",
    "play_preview": "Écouter la preview de",
    "pause_preview": "Mettre en pause la preview de",
    "seek_preview": "Choisir un passage dans la preview de",
    "preview_limit": "Preview limitée à {{ seconds }} secondes",
    "preview_status": "Preview limitée à 1 minute",
    "unavailable": "Indisponible",
    "audio_unavailable": "Preview audio indisponible",
    "coming": "Preview en cours d’ajout",
    "previous": "Preview précédente",
    "previous_title": "Précédent",
    "next": "Preview suivante",
    "next_title": "Suivant",
    "track_link": "Fiche beat",
    "dock_aria": "Lecteur OTTRecord",
    "loop_badge": "BOUCLE",
    "seek": "Position dans la preview",
    "controls": "Contrôles du lecteur",
    "loop": "Lire la preview en boucle",
    "loop_disable": "Désactiver la lecture en boucle",
    "loop_enabled": "Boucle activée",
    "loop_title": "Boucle",
    "close": "Fermer le lecteur",
    "close_title": "Fermer",
    "play_dock": "Lire la preview",
    "pause_dock": "Mettre la preview en pause",
    "play_title": "Lecture",
    "pause_title": "Pause",
    "playing": "Lecture",
    "looping": "Boucle active",
    "preview": "Preview",
    "error": "Preview indisponible",
    "tap_again": "Clique à nouveau pour écouter",
    "browser_blocked": "Lecture bloquée par le navigateur"
  };
  let audioLocale = {};
  try { audioLocale = JSON.parse(document.getElementById('OttPlayerLocaleV203')?.textContent || '{}'); } catch (_) {}
  const audioText = Object.freeze({ ...audioFallback, ...audioLocale });

  let audio;
  let activeTrigger;
  let activeWaveform;
  let currentSource = '';
  let currentTrack = null;
  let previewStart = 0;
  let previewLimit = 60;
  let dock;
  let queue = [];
  let loopEnabled = false;
  let restored = false;

  try {
    const savedLoop = localStorage.getItem(LOOP_KEY);
    const legacyLoop = localStorage.getItem(LEGACY_LOOP_KEY);
    loopEnabled = (savedLoop == null ? legacyLoop : savedLoop) === '1';
  } catch (_) {}

  const icons = {
    previous: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5v14M18 6.5 9.5 12l8.5 5.5z"/></svg>',
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 5v14M6 6.5l8.5 5.5L6 17.5z"/></svg>',
    loop: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17.5 7H8.2A4.2 4.2 0 0 0 4 11.2v.3M6.5 17H16a4 4 0 0 0 4-4v-.5M17 4l3 3-3 3M7 14l-3 3 3 3"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>'
  };

  const formatTime = (seconds) => {
    const value = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
  };

  const numeric = (value, fallback = 0) => {
    const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const sourceOf = (trigger) => trigger?.dataset.audio || trigger?.dataset.preview || '';

  const absoluteUrl = (value) => {
    try { return new URL(String(value || ''), window.location.href).href; } catch (_) { return String(value || ''); }
  };

  const normalizeTrack = (track) => {
    if (!track || typeof track !== 'object') return null;
    const source = String(track.source || '').trim();
    if (!source) return null;
    return {
      source,
      title: String(track.title || 'Instrumentale OTTRecord').slice(0, 180),
      meta: String(track.meta || 'Preview OTTBeatz').slice(0, 180),
      cover: String(track.cover || '').slice(0, 1000),
      link: String(track.link || '/pages/beats').slice(0, 1000),
      previewStart: Math.max(0, numeric(track.previewStart, 0)),
      previewLimit: Math.max(10, Math.min(MAX_PREVIEW, numeric(track.previewLimit, 60)))
    };
  };

  const trackFromTrigger = (trigger) => {
    if (!trigger) return null;
    const waveform = trigger.closest?.('[data-ott-waveform]');
    return normalizeTrack({
      source: sourceOf(trigger),
      title: trigger.dataset.title,
      meta: trigger.dataset.meta,
      cover: trigger.dataset.cover,
      link: trigger.dataset.link || trigger.closest?.('a[href]')?.href || '/pages/beats',
      previewStart: numeric(trigger.dataset.previewStart, numeric(waveform?.dataset.previewStart, 0)),
      previewLimit: numeric(trigger.dataset.previewLimit, numeric(waveform?.dataset.previewLimit, 60))
    });
  };

  const collectDocumentQueue = () => {
    const unique = new Map();
    document.querySelectorAll('[data-ott-play]').forEach((trigger) => {
      if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;
      const track = trackFromTrigger(trigger);
      if (track && !unique.has(track.source)) unique.set(track.source, track);
    });
    return [...unique.values()];
  };

  const readQueue = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(QUEUE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      const unique = new Map();
      parsed.forEach((item) => {
        const track = normalizeTrack(item);
        if (track && !unique.has(track.source)) unique.set(track.source, track);
      });
      return [...unique.values()].slice(0, 100);
    } catch (_) {
      return [];
    }
  };

  const writeQueue = () => {
    try { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(0, 100))); } catch (_) {}
  };

  const syncQueue = () => {
    const pageQueue = collectDocumentQueue();
    const stored = readQueue();
    if (pageQueue.length >= 2) {
      queue = pageQueue;
      writeQueue();
      return;
    }
    if (stored.length) {
      queue = stored;
      if (pageQueue.length === 1 && !queue.some((track) => track.source === pageQueue[0].source)) {
        queue = [pageQueue[0], ...queue].slice(0, 100);
        writeQueue();
      }
      return;
    }
    queue = pageQueue;
    writeQueue();
  };

  const findTriggerBySource = (source) => {
    if (!source) return null;
    return [...document.querySelectorAll('[data-ott-play]')].find((trigger) => sourceOf(trigger) === source) || null;
  };

  const setElementPlayingState = (element, playing) => {
    if (!element) return;
    element.classList.toggle('is-playing', playing);
  };

  const setTriggerState = (playing) => {
    document.querySelectorAll('[data-ott-play]').forEach((trigger) => {
      const ownsTrack = sourceOf(trigger) === currentSource;
      const active = Boolean(playing && ownsTrack);
      const icon = trigger.querySelector('[data-ott-waveform-icon]');
      if (icon) icon.textContent = active ? '❚❚' : '▶';
      else if (!trigger.querySelector('strong, .ott-waveform-player__play-label')) trigger.textContent = active ? '❚❚' : '▶';
      trigger.classList.toggle('is-playing', active);
      trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (ownsTrack) {
        const existing = trigger.getAttribute('aria-label') || '';
        const waveformLabel = trigger.querySelector('.ott-waveform-player__play-label');
        if (waveformLabel) waveformLabel.textContent = active ? audioText.pause : audioText.play;
        const sourceTitle = trigger.dataset.title || currentTrack?.title || '';
        if (sourceTitle) trigger.setAttribute('aria-label', (active ? audioText.pause_preview : audioText.play_preview) + ' ' + sourceTitle);
      }
      setElementPlayingState(trigger.closest('[data-ott-waveform]'), active);
      setElementPlayingState(trigger.closest('[data-beat-card], .ott-beat-card--app, [data-index-card]'), active);
      const status = trigger.closest('[data-ott-waveform]')?.querySelector('[data-ott-waveform-status]');
      if (status && ownsTrack) status.textContent = active ? (loopEnabled ? audioText.looping : audioText.playing) : audioText.preview;
    });
  };

  const deterministicPeaks = (seed, count) => {
    let state = 2166136261;
    String(seed || 'OTTRecord').split('').forEach((character) => {
      state ^= character.charCodeAt(0);
      state = Math.imul(state, 16777619);
    });
    return Array.from({ length: count }, (_, index) => {
      state ^= Math.imul(index + 1, 2654435761);
      state >>>= 0;
      return Math.max(14, Math.min(96, 22 + (state % 68)));
    });
  };

  const parsePeaks = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(String(raw).replace(/&quot;/g, '"'));
      if (Array.isArray(parsed)) return parsed.map(Number).filter(Number.isFinite);
    } catch (_) {}
    return String(raw).replace(/[\[\]"]/g, '').split(/[;,\s]+/).map(Number).filter(Number.isFinite);
  };

  const renderWaveform = (waveform) => {
    if (!waveform || waveform.dataset.ottR12WaveformReady === 'true') return;
    const bars = waveform.querySelector('[data-ott-waveform-bars]');
    if (!bars) return;
    let peaks = parsePeaks(waveform.dataset.waveformPeaks);
    if (!peaks.length) peaks = deterministicPeaks(waveform.dataset.waveformSeed, 48);
    const fragment = document.createDocumentFragment();
    peaks.slice(0, 84).forEach((height) => {
      const bar = document.createElement('i');
      bar.style.setProperty('--ott-wave-h', `${Math.max(8, Math.min(100, numeric(height, 30)))}%`);
      fragment.appendChild(bar);
    });
    bars.replaceChildren(fragment);
    waveform.dataset.ottR12WaveformReady = 'true';
  };

  const renderWaveforms = (scope = document) => scope.querySelectorAll('[data-ott-waveform]').forEach(renderWaveform);

  const updatePlayControl = () => {
    if (!dock) return;
    const button = dock.querySelector('[data-ott-r11-toggle]');
    if (!button) return;
    const playing = Boolean(audio && !audio.paused);
    button.innerHTML = playing ? icons.pause : icons.play;
    button.setAttribute('aria-label', playing ? audioText.pause_dock : audioText.play_dock);
    button.setAttribute('title', playing ? audioText.pause_title : audioText.play_title);
  };

  const updateLoopControl = () => {
    if (!dock) return;
    const button = dock.querySelector('[data-ott-r11-loop]');
    if (!button) return;
    button.classList.toggle('is-active', loopEnabled);
    button.setAttribute('aria-pressed', loopEnabled ? 'true' : 'false');
    button.setAttribute('aria-label', loopEnabled ? audioText.loop_disable : audioText.loop);
    button.setAttribute('title', loopEnabled ? audioText.loop_enabled : audioText.loop_title);
    const badge = dock.querySelector('[data-ott-r11-loop-state]');
    if (badge) badge.hidden = !loopEnabled;
  };

  const updateNavigationState = () => {
    if (!dock) return;
    const disabled = queue.length < 2;
    ['previous', 'next'].forEach((name) => {
      const button = dock.querySelector(`[data-ott-r11-${name}]`);
      if (button) button.disabled = disabled;
    });
  };

  const savePlaybackState = () => {
    if (!currentTrack) return;
    const resumeTime = audio && Number.isFinite(audio.currentTime) ? audio.currentTime : previewStart;
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({
        savedAt: Date.now(),
        track: currentTrack,
        resumeTime,
        wasPlaying: Boolean(audio && !audio.paused),
        open: Boolean(dock && !dock.hidden)
      }));
    } catch (_) {}
  };

  const ensureAudio = () => {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'metadata';
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('play', () => {
      setTriggerState(true);
      updatePlayControl();
      savePlaybackState();
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
    });
    audio.addEventListener('pause', () => {
      setTriggerState(false);
      updatePlayControl();
      savePlaybackState();
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    });
    audio.addEventListener('ended', handleNaturalEnd);
    audio.addEventListener('error', () => showStatus(audioText.error));
    return audio;
  };

  const ensureDock = () => {
    if (dock) return dock;
    dock = document.createElement('aside');
    dock.id = 'OttAudioDock';
    dock.className = 'ott-r11-audio-dock';
    dock.hidden = true;
    dock.dataset.ottAudioSkin = 'r11';
    dock.dataset.ottAudioEngine = VERSION;
    dock.setAttribute('aria-label', audioText.dock_aria);
    dock.innerHTML = `
      <div class="ott-r11-audio__cover" data-ott-r11-cover><span>OTT</span></div>
      <div class="ott-r11-audio__main">
        <div class="ott-r11-audio__topline">
          <span class="ott-r11-audio__brand">OTT PLAYER</span>
          <span class="ott-r11-audio__loop-state" data-ott-r11-loop-state hidden>BOUCLE</span>
          <a class="ott-r11-audio__link" data-ott-r11-link href="/pages/beats">Fiche beat</a>
        </div>
        <strong class="ott-r11-audio__title" data-ott-r11-title>Instrumentale OTTRecord</strong>
        <span class="ott-r11-audio__meta" data-ott-r11-meta>Preview OTTBeatz</span>
        <div class="ott-r11-audio__progress">
          <span data-ott-r11-current>0:00</span>
          <input type="range" min="0" max="1000" value="0" step="1" data-ott-r11-range aria-label="Position dans la preview">
          <span data-ott-r11-duration>1:00</span>
        </div>
      </div>
      <div class="ott-r11-audio__controls" role="group" aria-label="Contrôles du lecteur">
        <button type="button" data-ott-r11-previous aria-label="Preview précédente" title="Précédent">${icons.previous}</button>
        <button class="ott-r11-audio__toggle" type="button" data-ott-r11-toggle aria-label="Lire la preview" title="Lecture">${icons.play}</button>
        <button type="button" data-ott-r11-next aria-label="Preview suivante" title="Suivant">${icons.next}</button>
        <button class="ott-r11-audio__loop" type="button" data-ott-r11-loop aria-pressed="false" aria-label="Lire la preview en boucle" title="Boucle">${icons.loop}</button>
        <button class="ott-r11-audio__close" type="button" data-ott-r11-close aria-label="Fermer le lecteur" title="Fermer">${icons.close}</button>
      </div>`;
    dock.querySelector('[data-ott-r11-loop-state]').textContent = audioText.loop_badge;
    dock.querySelector('[data-ott-r11-link]').textContent = audioText.track_link;
    dock.querySelector('[data-ott-r11-range]').setAttribute('aria-label', audioText.seek);
    dock.querySelector('[data-ott-r11-previous]').setAttribute('aria-label', audioText.previous);
    dock.querySelector('[data-ott-r11-previous]').setAttribute('title', audioText.previous_title);
    dock.querySelector('[data-ott-r11-next]').setAttribute('aria-label', audioText.next);
    dock.querySelector('[data-ott-r11-next]').setAttribute('title', audioText.next_title);
    dock.querySelector('[data-ott-r11-loop]').setAttribute('aria-label', audioText.loop);
    dock.querySelector('[data-ott-r11-loop]').setAttribute('title', audioText.loop_title);
    dock.querySelector('[data-ott-r11-close]').setAttribute('aria-label', audioText.close);
    dock.querySelector('[data-ott-r11-close]').setAttribute('title', audioText.close_title);
    dock.querySelector('.ott-r11-audio__controls').setAttribute('aria-label', audioText.controls);
    document.body.appendChild(dock);
    dock.querySelector('[data-ott-r11-toggle]').addEventListener('click', toggleCurrent);
    dock.querySelector('[data-ott-r11-previous]').addEventListener('click', () => playAdjacent(-1));
    dock.querySelector('[data-ott-r11-next]').addEventListener('click', () => playAdjacent(1));
    dock.querySelector('[data-ott-r11-loop]').addEventListener('click', toggleLoop);
    dock.querySelector('[data-ott-r11-close]').addEventListener('click', closeDock);
    dock.querySelector('[data-ott-r11-range]').addEventListener('input', seekFromRange);
    updateLoopControl();
    updateNavigationState();
    updatePlayControl();
    return dock;
  };

  const showStatus = (message) => {
    const title = dock?.querySelector('[data-ott-r11-title]');
    if (title && message) title.textContent = message;
  };

  const updateMediaSession = (track) => {
    if (!track || !('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title,
        artist: track.meta || 'OTTBeatz',
        album: 'OTTRecord',
        artwork: track.cover ? [{ src: track.cover }] : []
      });
    } catch (_) {}
  };

  const updateDock = (track) => {
    if (!track) return;
    const playerDock = ensureDock();
    playerDock.querySelector('[data-ott-r11-title]').textContent = track.title;
    playerDock.querySelector('[data-ott-r11-meta]').textContent = track.meta;
    playerDock.querySelector('[data-ott-r11-link]').href = track.link || '/pages/beats';
    playerDock.querySelector('[data-ott-r11-duration]').textContent = formatTime(track.previewLimit);
    const coverRoot = playerDock.querySelector('[data-ott-r11-cover]');
    coverRoot.replaceChildren();
    if (track.cover) {
      const image = document.createElement('img');
      image.src = track.cover;
      image.alt = '';
      image.width = 128;
      image.height = 128;
      image.decoding = 'async';
      coverRoot.appendChild(image);
    } else {
      const mark = document.createElement('span');
      mark.textContent = 'OTT';
      coverRoot.appendChild(mark);
    }
    playerDock.hidden = false;
    document.body.classList.add('ott-r11-audio-open');
    updateNavigationState();
    updateLoopControl();
    updateMediaSession(track);
  };

  const bindCurrentDom = () => {
    activeTrigger = findTriggerBySource(currentSource);
    activeWaveform = activeTrigger?.closest('[data-ott-waveform]') || null;
    setTriggerState(Boolean(audio && !audio.paused));
    updateProgress();
  };

  function restartLoop() {
    if (!audio || !currentSource) return;
    try { audio.currentTime = previewStart; } catch (_) {}
    resetProgress(false);
    audio.play().catch(() => showStatus(audioText.tap_again));
  }

  function handleNaturalEnd() {
    if (loopEnabled) {
      restartLoop();
      return;
    }
    if (audio) {
      try { audio.currentTime = previewStart; } catch (_) {}
    }
    resetProgress(true);
    savePlaybackState();
  }

  function updateProgress() {
    if (!audio || !currentTrack) return;
    const elapsed = Math.max(0, audio.currentTime - previewStart);
    if (elapsed >= previewLimit) {
      if (loopEnabled) restartLoop();
      else {
        audio.pause();
        try { audio.currentTime = previewStart; } catch (_) {}
        resetProgress(true);
      }
      return;
    }
    const ratio = Math.max(0, Math.min(1, elapsed / previewLimit));
    if (dock) {
      dock.querySelector('[data-ott-r11-range]').value = String(Math.round(ratio * 1000));
      dock.querySelector('[data-ott-r11-current]').textContent = formatTime(elapsed);
    }
    if (activeWaveform && activeWaveform.isConnected) {
      activeWaveform.style.setProperty('--ott-wave-progress', `${ratio * 100}%`);
      const current = activeWaveform.querySelector('[data-ott-waveform-current]');
      if (current) current.textContent = formatTime(elapsed);
    }
  }

  const resetProgress = (setPausedState = false) => {
    if (dock) {
      dock.querySelector('[data-ott-r11-range]').value = '0';
      dock.querySelector('[data-ott-r11-current]').textContent = '0:00';
    }
    document.querySelectorAll('[data-ott-waveform]').forEach((waveform) => {
      waveform.style.setProperty('--ott-wave-progress', '0%');
      const current = waveform.querySelector('[data-ott-waveform-current]');
      if (current) current.textContent = '0:00';
    });
    if (setPausedState) setTriggerState(false);
  };

  const activateTrack = (track, trigger = null) => {
    const normalized = normalizeTrack(track);
    if (!normalized) return null;
    currentTrack = normalized;
    currentSource = normalized.source;
    previewStart = normalized.previewStart;
    previewLimit = normalized.previewLimit;
    activeTrigger = trigger || findTriggerBySource(currentSource);
    activeWaveform = activeTrigger?.closest('[data-ott-waveform]') || null;
    if (!queue.some((item) => item.source === normalized.source)) {
      queue = [normalized, ...queue].slice(0, 100);
      writeQueue();
    }
    updateDock(normalized);
    return normalized;
  };

  const startTrack = async (track, trigger = null, forcePlay = false) => {
    const normalized = normalizeTrack(track);
    if (!normalized) return;
    const player = ensureAudio();
    const sameSource = normalized.source === currentSource;
    if (sameSource && !player.paused && !forcePlay) {
      player.pause();
      return;
    }

    activateTrack(normalized, trigger);

    if (!sameSource || absoluteUrl(player.src) !== absoluteUrl(normalized.source)) {
      player.src = normalized.source;
      player.load();
      const positionAtStart = () => {
        if (normalized.source !== currentSource) return;
        try { player.currentTime = previewStart; } catch (_) {}
      };
      if (player.readyState >= 1) positionAtStart();
      else player.addEventListener('loadedmetadata', positionAtStart, { once: true });
      resetProgress(false);
    } else if (player.currentTime < previewStart || player.currentTime >= previewStart + previewLimit) {
      try { player.currentTime = previewStart; } catch (_) {}
    }

    savePlaybackState();
    try {
      await player.play();
    } catch (_) {
      showStatus(audioText.tap_again);
      setTriggerState(false);
    }
  };

  const startPreview = (trigger, forcePlay = false) => {
    const track = trackFromTrigger(trigger);
    if (!track) return Promise.resolve();
    return startTrack(track, trigger, forcePlay);
  };

  function toggleCurrent() {
    if (!currentTrack) return;
    const player = ensureAudio();
    if (!player.src || absoluteUrl(player.src) !== absoluteUrl(currentTrack.source)) {
      startTrack(currentTrack, activeTrigger, true);
      return;
    }
    if (player.paused) player.play().catch(() => showStatus(audioText.browser_blocked));
    else player.pause();
  }

  function toggleLoop() {
    loopEnabled = !loopEnabled;
    try { localStorage.setItem(LOOP_KEY, loopEnabled ? '1' : '0'); } catch (_) {}
    updateLoopControl();
    if (audio && !audio.paused) setTriggerState(true);
  }

  function playAdjacent(delta) {
    if (!queue.length) syncQueue();
    if (!queue.length) return;
    let index = queue.findIndex((track) => track.source === currentSource);
    if (index < 0) index = delta > 0 ? -1 : 0;
    const nextIndex = (index + delta + queue.length) % queue.length;
    const nextTrack = queue[nextIndex];
    startTrack(nextTrack, findTriggerBySource(nextTrack.source), true);
  }

  function seekFromRange(event) {
    if (!audio || !currentTrack) return;
    const ratio = numeric(event.target.value, 0) / 1000;
    audio.currentTime = previewStart + (ratio * previewLimit);
    updateProgress();
    savePlaybackState();
  }

  const seekWaveform = (rail, event) => {
    const waveform = rail.closest('[data-ott-waveform]');
    const trigger = waveform?.querySelector('[data-ott-play]');
    if (!trigger) return;
    const rect = rail.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width)));
    const track = trackFromTrigger(trigger);
    if (!track) return;
    if (track.source !== currentSource) startTrack(track, trigger, true).then(() => {
      if (audio) {
        audio.currentTime = previewStart + (ratio * previewLimit);
        updateProgress();
      }
    });
    else if (audio) {
      audio.currentTime = previewStart + (ratio * previewLimit);
      updateProgress();
    }
  };

  function closeDock() {
    if (audio) {
      audio.pause();
      try { audio.currentTime = previewStart; } catch (_) {}
    }
    resetProgress(true);
    if (dock) dock.hidden = true;
    document.body.classList.remove('ott-r11-audio-open');
    savePlaybackState();
  }

  const restorePlaybackState = () => {
    if (restored) return;
    restored = true;
    let state;
    try { state = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null'); } catch (_) { state = null; }
    if (!state || !state.savedAt || Date.now() - state.savedAt > STATE_MAX_AGE || state.open === false) return;
    const track = normalizeTrack(state.track);
    if (!track) return;
    activateTrack(track, findTriggerBySource(track.source));
    const player = ensureAudio();
    player.src = track.source;
    player.load();
    const resumeTime = Math.max(track.previewStart, Math.min(track.previewStart + track.previewLimit - 0.25, numeric(state.resumeTime, track.previewStart)));
    const applyResume = () => {
      try { player.currentTime = resumeTime; } catch (_) {}
      updateProgress();
    };
    if (player.readyState >= 1) applyResume();
    else player.addEventListener('loadedmetadata', applyResume, { once: true });
    updatePlayControl();
    setTriggerState(false);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ott-play]');
    if (trigger) {
      event.preventDefault();
      startPreview(trigger);
      return;
    }
    const rail = event.target.closest('[data-ott-waveform-seek]');
    if (rail) {
      event.preventDefault();
      seekWaveform(rail, event);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && dock && !dock.hidden) closeDock();
  });

  if ('mediaSession' in navigator) {
    try {
      navigator.mediaSession.setActionHandler('play', () => toggleCurrent());
      navigator.mediaSession.setActionHandler('pause', () => audio?.pause());
      navigator.mediaSession.setActionHandler('previoustrack', () => playAdjacent(-1));
      navigator.mediaSession.setActionHandler('nexttrack', () => playAdjacent(1));
    } catch (_) {}
  }

  const boot = (scope = document) => {
    renderWaveforms(scope);
    syncQueue();
    if (currentSource) bindCurrentDom();
    if (dock) updateNavigationState();
    restorePlaybackState();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('ott:navigation:complete', (event) => {
    boot(event.detail?.main || document);
    bindCurrentDom();
  });
  document.addEventListener('ott:catalogue:updated', () => boot());
  window.addEventListener('pagehide', savePlaybackState);

  window.OTTAudioHub = {
    version: VERSION,
    pause: () => audio?.pause(),
    close: closeDock,
    next: () => playAdjacent(1),
    previous: () => playAdjacent(-1),
    setLoop: (value) => {
      loopEnabled = Boolean(value);
      try { localStorage.setItem(LOOP_KEY, loopEnabled ? '1' : '0'); } catch (_) {}
      updateLoopControl();
    },
    renderWaveforms,
    isPlaying: () => Boolean(audio && !audio.paused),
    getState: () => ({
      source: currentSource,
      playing: Boolean(audio && !audio.paused),
      loop: loopEnabled,
      queueLength: queue.length,
      track: currentTrack
    })
  };
})();
